import { IWidgetControllerConfig } from '@wix/native-components-infra/dist/src/types/types';
import {
  PaymentType,
  V1Participant,
} from '@wix/ambassador-challenge-service-web/types';
import { IPromptLogin, IUserType, UserState } from '../UserContext';
import { ControllerFlowAPI } from 'yoshi-flow-editor-runtime/build/cjs/flow-api/ViewerScript';
import { getParticipant } from './getParticipant';
import format from 'date-fns/format';
import { getDateFromString } from '../../../selectors/dates';
import { SelectedPaymentOption } from '../../../components/ChallengesPage/Widget/components/Pricing/interfaces';
import {
  getChallengeSlugFromLocation,
  locationProviderPropsMap,
} from '../../Location/locationProviderPropsMap';
import { Pages } from '../../Location/LocationContext';
import { IUserProviderProps } from '../UserProvider';
import app from '../../../../.application.json';
import { syncInstance } from '../../../services/instance';

export const getUserType = (
  user: { loggedIn: boolean },
  participant: V1Participant,
): IUserType => {
  if (!user.loggedIn) {
    return UserState.VISITOR;
  }

  if (!participant) {
    return UserState.MEMBER;
  }

  return participant.transitions[0].state;
};

export async function promptLogin(flowAPI: ControllerFlowAPI) {
  const { language } = flowAPI?.environment;

  await flowAPI.controllerConfig.wixCodeApi.user.promptLogin({
    lang: language || 'en',
  } as IPromptLogin);
}

export type UpdatedUserData = Pick<
  IUserProviderProps,
  'user' & 'participant' & 'userType'
>;

export async function updateUserContext(
  flowAPI: ControllerFlowAPI,
  newParticipant?: V1Participant,
): Promise<UpdatedUserData> {
  const user = getUserFromConfig(flowAPI.controllerConfig);
  const participant: V1Participant =
    newParticipant || (await getParticipant(flowAPI));

  return {
    user,
    participant,
    userType: getUserType(user, participant),
  };
}

export async function handleUserLogin(
  flowAPI: ControllerFlowAPI,
  userProvider: IUserProviderProps,
) {
  flowAPI.controllerConfig.wixCodeApi.user.onLogin(async () => {
    syncInstance(flowAPI, true);

    await userProvider.updateParticipant();
  });
}

export function getUserFromConfig(config: IWidgetControllerConfig) {
  return {
    ...config.wixCodeApi.user.currentUser,
    instance: config.wixCodeApi.site.getAppToken(app.appDefinitionId),
  };
}

export const toServerStartDate = (startDate?: string) => {
  return startDate ? format(getDateFromString(startDate), 'yyyy-MM-dd') : null;
};

export const getTimeZone = (flowAPI: ControllerFlowAPI) => {
  return typeof Intl === 'object' && typeof Intl.DateTimeFormat === 'function'
    ? Intl.DateTimeFormat().resolvedOptions().timeZone
    : flowAPI.controllerConfig.wixCodeApi.site.timezone;
};

export function getPaymentType(
  selectedPaymentOption: SelectedPaymentOption,
): PaymentType {
  switch (selectedPaymentOption) {
    case SelectedPaymentOption.PaidPlans:
      return PaymentType.PAID_PLANS;
    default:
    case SelectedPaymentOption.SinglePayment:
      return PaymentType.SINGLE_PAYMENT;
  }
}

export async function navigateToThankYouPage(flowAPI: ControllerFlowAPI) {
  const location = locationProviderPropsMap(flowAPI);
  return location.goToPage({
    challengeId: getChallengeSlugFromLocation(flowAPI),
    pageId: Pages.ThankYou,
  });
}
